import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { ErrorMessages, Rules } from '../../config/types';
import { textinputMaxLength } from '../../constants';
import {
  containsReservedCharacters,
  getInputRules,
  stripReservedCharacters,
  validateField,
} from '../../utils/form-utils';
import { parseDecimalPlacesRule } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
import Input from './TextInput';

const stripFormatting = (value: string) => value.replace(/,/g, '').trim();

const isDigitsOnly = (value: string) => /^\d*$/.test(value);

const isDecimalWithPrecision = (value: string, decimalPlaces: number) =>
  new RegExp(`^\\d*(?:\\.\\d{0,${decimalPlaces}})?$`).test(value);

const isScientificNotation = (value: string) => /^-?\d+(?:\.\d+)?e[+-]?\d+$/i.test(value);

const formatNumberWithCommas = (value: string | number) => {
  if (value === null || value === undefined || value === '') return '';
  const normalized = stripFormatting(String(value));
  if (!normalized) return '';

  if (isScientificNotation(normalized)) {
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      return parsed.toLocaleString('en-GB', { maximumFractionDigits: 20 });
    }
  }

  const [whole = '', fraction] = normalized.split('.', 2);
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (fraction !== undefined) {
    return `${formattedWhole || '0'}.${fraction}`;
  }

  return formattedWhole;
};

const displayIndexToRawIndex = (displayValue: string, displayIndex: number) =>
  stripFormatting(displayValue.slice(0, displayIndex)).length;

const rawIndexToDisplayIndex = (displayValue: string, rawIndex: number) => {
  let currentRawIndex = 0;

  for (let i = 0; i < displayValue.length; i += 1) {
    if (displayValue[i] !== ',') {
      if (currentRawIndex === rawIndex) {
        return i;
      }
      currentRawIndex += 1;
    }
  }

  return displayValue.length;
};

const sanitizePastedNumericValue = ({
  currentValue,
  pastedValue,
  selectionStart,
  selectionEnd,
  allowDecimal,
  decimalPlaces,
  reservedCharacters,
}: {
  currentValue: string;
  pastedValue: string;
  selectionStart: number;
  selectionEnd: number;
  allowDecimal: boolean;
  decimalPlaces: number;
  reservedCharacters?: string;
}) => {
  const prefix = currentValue.slice(0, selectionStart);
  const suffix = currentValue.slice(selectionEnd);
  const strippedPaste = stripFormatting(
    stripReservedCharacters(pastedValue, reservedCharacters),
  );

  let acceptedPaste = '';

  for (const char of strippedPaste) {
    if (!/[0-9.]/.test(char)) {
      continue;
    }

    const candidatePaste = `${acceptedPaste}${char}`;
    const candidateValue = `${prefix}${candidatePaste}${suffix}`;
    const isValid = allowDecimal
      ? isDecimalWithPrecision(candidateValue, decimalPlaces)
      : isDigitsOnly(candidateValue);

    if (isValid) {
      acceptedPaste = candidatePaste;
    }
  }

  return {
    nextValue: `${prefix}${acceptedPaste}${suffix}`,
    acceptedLength: acceptedPaste.length,
  };
};

type Props = {
  name: string;
  title: string;
  width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter';
  rules?: Rules;
  txt: string;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  inputHeading?: string;
} & ComponentPropsWithoutRef<'input'>;

const CostInputField = (props: Props) => {
  const {
    name,
    title,
    rules,
    disabled,
    defaultValue,
    errorMessages,
    detailsHeading,
    detailsContent,
    inputHeading,
  } = props;
  const { register, getFieldState, getValues, setValue, control } = useFormContext();
  const decimalPlaces = parseDecimalPlacesRule(rules?.decimal);
  const allowDecimal = typeof decimalPlaces === 'number';
  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textinputMaxLength) }),
    [rules, title],
  );

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string | number) => {
      const valueAsString = typeof value === 'number' ? String(value) : String(value ?? '');
      return validateField(title, inputRules, stripFormatting(valueAsString), errorMessages);
    },
  });
  const { error } = getFieldState(name);
  const { onBlur: handleBlur } = hooksFormRegister;
  const rawValue = useWatch({ control, name });
  const displayValue = formatNumberWithCommas(rawValue ?? '');

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-form-group">
        {inputHeading && (
          <label className="govuk-label" htmlFor="instances">
            {inputHeading}
          </label>
        )}
        <div className="govuk-input__wrapper">
          <input
            type="hidden"
            {...hooksFormRegister}
            value={typeof rawValue === 'string' || typeof rawValue === 'number' ? rawValue : ''}
          />
          <div className="govuk-input__prefix" aria-hidden="true">
            £
          </div>
          <Input
            {...props}
            disabled={disabled ?? false}
            value={displayValue}
            onChange={(e) => {
              const normalized = stripFormatting(e.target.value);
              const isValid = allowDecimal
                ? isDecimalWithPrecision(normalized, decimalPlaces)
                : isDigitsOnly(normalized);

              if (!isValid) {
                return;
              }

              setValue(name, normalized, {
                shouldValidate: true,
                shouldDirty: true,
              });
              props.onChange?.(e);
            }}
            onBlur={(e) => {
              handleBlur(e);
              props.onBlur?.(e);
            }}
            onKeyDown={(e) => {
              // The e.key values are strings for control characters such as 'Backspace'
              // All non control characters will have a single character length
              if (
                e.key.length === 1 &&
                containsReservedCharacters(e.key, inputRules.reservedCharacters)
              ) {
                e.preventDefault();
                return;
              }

              const allowedNav = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Home',
                'End',
              ];
              if (allowedNav.includes(e.key) || e.ctrlKey || e.metaKey) return;

              if (allowDecimal) {
                if (e.key === '-') {
                  e.preventDefault();
                  return;
                }

                const currentValue = stripFormatting(
                  typeof rawValue === 'string' || typeof rawValue === 'number'
                    ? String(rawValue)
                    : '',
                );
                const selectionStart = e.currentTarget.selectionStart ?? currentValue.length;
                const selectionEnd = e.currentTarget.selectionEnd ?? currentValue.length;
                const nextValue = `${currentValue.slice(0, selectionStart)}${e.key}${currentValue.slice(
                  selectionEnd,
                )}`;
                if (!isDecimalWithPrecision(nextValue, decimalPlaces)) {
                  e.preventDefault();
                }
                return;
              }

              if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();

              const target = e.currentTarget;
              const currentValue =
                typeof rawValue === 'string' || typeof rawValue === 'number'
                  ? String(rawValue)
                  : '';
              const selectionStart = displayIndexToRawIndex(
                target.value,
                target.selectionStart ?? target.value.length,
              );
              const selectionEnd = displayIndexToRawIndex(
                target.value,
                target.selectionEnd ?? target.value.length,
              );
              const { nextValue, acceptedLength } = sanitizePastedNumericValue({
                currentValue,
                pastedValue: e.clipboardData.getData('text'),
                selectionStart,
                selectionEnd,
                allowDecimal,
                decimalPlaces: decimalPlaces ?? 0,
                reservedCharacters: inputRules.reservedCharacters,
              });

              setValue(name, nextValue, {
                shouldValidate: true,
                shouldDirty: true,
              });

              requestAnimationFrame(() => {
                const formattedNextValue = formatNumberWithCommas(nextValue);
                const nextRawCursor = selectionStart + acceptedLength;
                const nextDisplayCursor = rawIndexToDisplayIndex(
                  formattedNextValue,
                  nextRawCursor,
                );
                target.selectionStart = nextDisplayCursor;
                target.selectionEnd = nextDisplayCursor;
              });

              props.onPaste?.(e);
            }}
            width="one-quarter"
            type="text"
            inputMode={allowDecimal ? 'decimal' : 'numeric'}
          />
        </div>
      </div>
    </>
  );
};
CostInputField.displayName = 'CostInputField';

export default CostInputField;
